import { type LiveChannel } from '$/channels'
import { State, rangePosition, type RangePosition } from '$/epg/programs'
import { LiveChannelRegion } from '@setplex/tria-api'
import { CategoryId, TvCategoryEx } from '~/entities/epg/categories'
import { parseEpgPath } from '~/entities/epg/lib'
import { type LiveProgramInfo } from '~/features/epg/programs/index.h'
import {
  formatDateDuration,
  formatDateToLabelInfo,
  getSearchParams,
  timeWithin,
} from '~/shared/helpers'
import type { IChannelInfo, LiveProgramLocalizedInfo } from './index.h'
/**
 * Gets information about the current channel including its index in channels list,
 * channel object and total count of channels in list
 *
 * @param {Object} params - The parameters object
 * @param {number | undefined} params.channelId - Optional current channel id
 * @param {string} params.searchParams - URL search parameters string
 * @param {LiveChannel[]} params.channels - Array of live channels
 *
 * @returns {IChannelInfo | null} Object containing channel info or null if no valid channel id found
 */

export const getCurrentChannelInfo = ({
  region,
  channelId,
  pathname = '',
  searchParams,
  categories,
  channels,
}: {
  region?: LiveChannelRegion | null
  channelId?: number | null
  pathname?: string
  searchParams?: string
  categories: TvCategoryEx[]
  channels: LiveChannel[]
}): IChannelInfo | null => {
  const {
    categoryId: categoryIdFromSearchParams,
    regionId: regionIdFromSearchParams,
  } = getSearchParams(searchParams ?? '')

  const { categoryId: categoryIdFromPathname } = parseEpgPath(pathname) ?? {}

  // if regionId or categoryId are provided (EPG page), use it, otherwise use them from search params (on live Player page)
  const regId = region?.id ?? regionIdFromSearchParams ?? null
  const categoryId =
    categoryIdFromPathname ?? categoryIdFromSearchParams ?? null
  const catId =
    categoryId === CategoryId.Favorite || categoryId === CategoryId.All
      ? categoryId
      : Number(categoryId)

  if (!channelId) return null

  const currentIndex = channels.findIndex(
    (channel: LiveChannel) => channel.id === Number(channelId)
  )
  const currentTvChannel = channels[currentIndex]
  const channelsCount = channels.length

  const category = categories?.find((category) => category.id === catId) ?? null

  return {
    currentIndex,
    currentTvChannel,
    channelsCount,
    category,
    channel: currentTvChannel,
    regionId:
      regId === null || regId === undefined || isNaN(Number(regId))
        ? null
        : String(regId),
    regionTitle: region?.name ?? '',
  }
}

export const formatStartEnd = ({
  actualStartMs,
  actualStopMs,
  leftMs,
  position,
}: Readonly<{
  actualStartMs: number
  actualStopMs: number
  leftMs: number
  position: RangePosition
}>): { startEndLabel: string; timeLeftLabel?: string; liveLabel?: string } => {
  const startEndLabel = formatDateToLabelInfo(actualStartMs, actualStopMs)

  // for live program format labels as [start-end, left time, live]
  if (position.state === State.Ongoing) {
    const { hours, minutes } = timeWithin(leftMs)
    const formattedDuration = formatDateDuration({
      duration: { hours, minutes },
      short: true,
    })

    return {
      startEndLabel,
      timeLeftLabel: `${formattedDuration} left`,
      liveLabel: 'live',
    }
  }

  // for all other cases (past, future) format labels as [start-end]
  return { startEndLabel }
}

/**
 * Function returns a percentage of how much of the video has already been played
 * @param actualDurationMs
 * @param leftMs
 */
export const getPercentageProgress = ({
  actualDurationMs,
  leftMs,
}: Readonly<{
  actualDurationMs: number
  leftMs: number
}>): number => ((actualDurationMs - leftMs) / actualDurationMs) * 100

export const formatProgramToLocalizedView =
  ({ now }: { now: number }) =>
  (info: LiveProgramInfo): LiveProgramLocalizedInfo => {
    const position = rangePosition([info.actualStartMs, info.actualStopMs], now)
    const isLive = position.state === State.Ongoing
    const leftMs = info.actualStopMs - now
    const progress = isLive
      ? getPercentageProgress({
          actualDurationMs: info.actualDurationMs,
          leftMs,
        })
      : 0

    return {
      ...info,
      isLive,
      progress,
      labels: formatStartEnd({
        actualStartMs: info.actualStartMs,
        actualStopMs: info.actualStopMs,
        leftMs,
        position,
      }),
    }
  }
